import { useMemo } from 'react';
import { useMatches } from 'react-router';
import { trackError } from '@gonfalon/telemetry/trackError';

import type { Statement } from './types';

/**
 * Hook to extract the activity spec from the current route's handle.
 *
 * Routes can define `handle.activity` as either:
 * - A static Statement[] array
 * - A function (params, data) => Statement[]
 *
 * This hook walks up the route tree (reversed matches) and returns
 * the first matching activity spec found.
 *
 * @returns The activity spec from the route handle, or undefined if not found
 */
export function useRouteActivitySpec(): Statement[] | undefined {
  const matches = useMatches();

  return useMemo(() => {
    const reversedMatches = [...matches].reverse();

    for (const match of reversedMatches) {
      const handle = match.handle as { activity?: unknown } | undefined;

      if (!handle || !('activity' in handle)) {
        continue;
      }

      const activity = handle.activity;

      // Handle function form: activity({ params, data }) => Statement[]
      if (typeof activity === 'function') {
        try {
          const spec = activity({ params: match.params, data: match.loaderData }) as Statement[];
          // Return spec if non-empty, otherwise undefined - this route claims ownership
          return Array.isArray(spec) && spec.length > 0 ? spec : undefined;
        } catch (error) {
          if (error instanceof Error) {
            trackError(error, { severity: 'medium' });
          }
          // Error means this route tried but failed - return undefined, don't fall through
          return undefined;
        }
      }

      // Handle static form: activity: Statement[]
      // Return even if empty - this route claims ownership
      if (Array.isArray(activity)) {
        return activity.length > 0 ? (activity as Statement[]) : undefined;
      }
    }

    return undefined;
  }, [matches]);
}
